import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IErrorDescription } from '@ibank-globals/error';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators/finalize';
import { ErrorHandler } from '../../../globals/classes/errorHandler';
import { RSAKey } from '../../../globals/classes/rsa/rsaKey';
import { AppSettings } from '../../app.settings';
import { ICreateApplicationRequest } from '../../classes/existing-onboarding-customer/requests';
import { ISignInRequest } from '../../classes/http-requests';
import {
	ITwoFactorAuthenticationInfo,
	TwoFactorAuthenticationInfo,
} from '../../classes/http-responses';
import { IInitializePasswordRequest } from '../../classes/initialize-password';
import { Extensions } from '../../classes/static/extensions';
import { GoogleAnalyticsUtils } from '../../classes/static/google-analytics-utils';
import { UserAuth } from '../../classes/userAuth';
import { TwoFAModalComponent } from '../two-fa-modal/two-fa-modal.component';
import { BaseComponent } from '../../../globals/classes/base.component';

@Component({
	selector: 'reset-password',
	template: require('./reset-password.component.html'),
})
export class ResetPasswordComponent extends BaseComponent implements OnInit {
	hasAcceptTerms: boolean = false;
	beforeCode: boolean = false;
	getCode: boolean = false;

	@Input() initRequest: IInitializePasswordRequest;
	@Input() ibUsernameRequest: string;
	@Input() createAppRequest: ICreateApplicationRequest;
	@Input() isOnboardingFlow: boolean;

	public signInRequest: ISignInRequest;
	public isLoading: boolean = false;
	public dynatraceForExistingOnBoarding: boolean = AppSettings.dynatraceForExistingOnBoarding;
	public isRebrading = AppSettings.isRebrading;

	constructor(
		private activeModal: NgbActiveModal,
		private modalService: NgbModal,
		private route: ActivatedRoute
	) {
		super();
	}

	public ngOnInit() {
		this.initRequests();

		this.route.queryParams.subscribe((params) => {
			this.signInRequest.returnUrl = Extensions.getReturnUrl(params)
				? Extensions.getReturnUrl(params)
				: Extensions.returnUrl;
			Extensions.returnUrl = this.signInRequest.returnUrl;
		});
	}

	public closeModal(): void {
		this.activeModal.close();
	}

	public connectNow(): void {
		if (this.dynatraceForExistingOnBoarding)
			this.setDynatraceEvent('ECO_Connect_Internet_Banking');
		this.activeModal.close();
		this.performLogin();
	}

	private initRequests(): void {
		this.signInRequest = {
			username: undefined,
			password: undefined,
			rememberLogin: undefined,
			returnUrl: undefined,
			userID: undefined,
		};
	}

	private handleError(error: IErrorDescription): void {
		ErrorHandler.newError(error);
	}

	private performLogin() {
		if (!this.initRequest.passwordNew) return;
		this.isLoading = true;
		this.signInRequest.username = this.ibUsernameRequest;
		this.signInRequest.rememberLogin = false;

		if (AppSettings.enforcePasswordEncryption) {
			let rsaKey = new RSAKey();
			rsaKey.setPublic(AppSettings.getPropertyN(), AppSettings.getPropertyE());
			this.signInRequest.password = rsaKey.encrypt(this.initRequest.passwordNew);
		} else {
			this.signInRequest.password = this.initRequest.passwordNew;
		}

		GoogleAnalyticsUtils.sendLoginEvent();

		UserAuth.signIn(this.signInRequest)
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe(
				(result) => this.handleSignInResponse(result),
				(error) => {
					this.handleError(error);
				}
			);
	}

	private handleSignInResponse(response: ITwoFactorAuthenticationInfo) {
		if (!response) return;
		this.goToCodeVerification(response);
	}

	private goToCodeVerification(twofaInfo: ITwoFactorAuthenticationInfo): void {
		const modalRef = this.modalService.open(TwoFAModalComponent, {
			windowClass: 'modal-small',
		});
		(modalRef.componentInstance as TwoFAModalComponent).twoFAInfo =
			new TwoFactorAuthenticationInfo(twofaInfo);
	}
}
